import React, { useEffect, useState } from 'react';

import { Form, Row, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import { Home } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { searchTimeOffRequests, fetchDriverOptions } from './operations';
import TimeOffRequestsTable from './TimeOffRequestsTable';
import TOR_STATUSES, { TOR_STATUS_LABELS } from './utils/TOR_STATUSES';
import { SubHeader, Breadcrumbs, Button } from '../../components';
import TimeOffRequestWindow from './TimeOffRequestWindow';
import './styles.scoped.css'

const statusOptions = [
  { value: TOR_STATUSES.PENDING_APPROVAL, label: TOR_STATUS_LABELS[TOR_STATUSES.PENDING_APPROVAL] },
  { value: TOR_STATUSES.APPROVED, label: TOR_STATUS_LABELS[TOR_STATUSES.APPROVED] },
  { value: TOR_STATUSES.DECLINED, label: TOR_STATUS_LABELS[TOR_STATUSES.DECLINED] },
  { value: TOR_STATUSES.CANCELLED, label: TOR_STATUS_LABELS[TOR_STATUSES.CANCELLED] },
];

const initialSearchState = {
  driverNameNumber: '',
  status: '',
  startDateFrom: '',
  startDateTo: '',
  requestDateFrom: '',
  requestDateTo: '',
};


const SearchTimeOffRequests = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [searchCriteria, setSearchCriteria] = useState(initialSearchState);
  const [isExporting, setIsExporting] = useState(false);
  const [driverOptions, setDriverOptions] = useState([]);
  const [driversLoading, setDriversLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetchDriverOptions()
      .then(opts => { if (!cancelled) setDriverOptions(opts); })
      .catch(() => { })
      .finally(() => { if (!cancelled) setDriversLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const handleSearch = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await searchTimeOffRequests(searchCriteria);
      setData(response);
      setShowResults(true);
    } catch (err) {
      console.error('Error searching time off requests:', err);
      setError('Failed to search time off requests. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSearchCriteria(initialSearchState);
    setData([]);
    setShowResults(false);
    setError(null);
  };

  const handleExportToExcel = async () => {
    setIsExporting(true);
    try {
      const headers = [
        'Date Entered',
        'Driver Name',
        'Driver Number',
        'Client Name',
        'Client Number',
        'Start Date',
        'End Date',
        'Reason',
        'Driver Note',
        'Service Comments',
        'Status',
      ];
      const csvRows = [headers.join(',')];

      data.forEach(request => {
        const row = [
          request.createdAt ? new Date(request.createdAt).toLocaleDateString() : '',
          request.employeeName || '',
          request.employeeId || '',
          request.customerName || '',
          request.customerId || '',
          request.startDate ? new Date(request.startDate).toLocaleDateString() : '',
          request.endDate ? new Date(request.endDate).toLocaleDateString() : '',
          request.reason || '',
          request.employeeComment || '',
          request.serviceComment || '',
          request.status || '',
        ].map(value => `"${value}"`).join(',');
        csvRows.push(row);
      });

      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'time-off-requests-search-results.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setError('Failed to export data. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleGoToRequest = (request) => {
    const statusRouteMap = {
      [TOR_STATUSES.PENDING_APPROVAL]: 'pending-approval',
      [TOR_STATUSES.APPROVED]: 'approved',
      [TOR_STATUSES.DECLINED]: 'declined',
      [TOR_STATUSES.CANCELLED]: 'cancelled',
    };
    const statusRoute = statusRouteMap[request.status] || 'pending-approval';
    const driverParam = `${request.employeeName} - ${request.employeeId}`;
    navigate(`/time-off-requests/${statusRoute}?viewAll=true&driver=${encodeURIComponent(driverParam)}`);
  };

  const customStyles = width => ({
    control: provided => ({
      ...provided,
      width: width,
      minHeight: '30px',
    }),
    menuList: provided => ({
      ...provided,
      maxHeight: '200px',
    }),
  });

  return (
    <div>
      <SubHeader
        text="Time Off Requests"
        buttons={[
          { icon: <Home sx={{ color: '#fff', fontSize: 30 }} />, onPress: () => navigate('/time-off-requests') },
        ]} />
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '30px',
        }}>
        <Breadcrumbs
          leftTitle="Time Off Requests"
          middleTitle="Search Requests"
          midItemAction={showResults ? () => setShowResults(false) : undefined}
          rightTitle={showResults ? 'Search Results' : undefined}
          route="/time-off-requests"
        />
        {!showResults && (
          <div style={{ marginRight: 35, marginTop: 30 }}>
            <Button
              variant="primary"
              style={{
                fontSize: '14px',
                maxWidth: '115px',
                padding: '16px',
                textAlign: 'center',
                marginRight: '8px',
              }}
              onPress={handleSearch}
              text="Search"
            />
            <Button
              variant="neutral"
              style={{
                fontSize: '14px',
                maxWidth: '115px',
                padding: '16px',
                textAlign: 'center',
                marginRight: '8px',
              }}
              onPress={handleReset}
              text="Reset Filters"
            />
          </div>
        )}
      </div>
      {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        {!showResults && (
          <TimeOffRequestWindow
            containerStyle={{ border: '2px solid #343a40' }}
            windowTitle="Search"
            headerStyle={{ backgroundColor: '#343a40' }}
            windowContent={
              <Form noValidate onSubmit={() => { }}>
                    <div className="filters-row">
                      <Row className="top-row">
                        <div
                          className="top-col"
                          style={{
                            width: '65%',
                            marginTop: '30px',
                            paddingLeft: '15px',
                          }}>
                          <Form.Group as={Row} controlId="driverNameNumber">
                            <Form.Label style={{ marginTop: '15px', width: '290px' }}>
                              <div>Driver Name &amp; Number</div>
                            </Form.Label>
                            <div className="dropdown-spinner-container">
                              <Select
                                styles={customStyles('483px')}
                                options={driverOptions}
                                isLoading={driversLoading}
                                isClearable
                                onChange={(event, action) => {
                                  if (action.action === 'clear') {
                                    setSearchCriteria(prev => ({ ...prev, driverNameNumber: '' }));
                                  }
                                  if (action.action === 'select-option') {
                                    setSearchCriteria(prev => ({ ...prev, driverNameNumber: event.value }));
                                  }
                                }}
                                value={driverOptions?.filter(opt => opt.value === searchCriteria.driverNameNumber)}
                              />
                              <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                aria-hidden="true"
                                className="dropdown-spinner"
                                hidden={!driversLoading}
                              />
                            </div>
                          </Form.Group>
                        </div>
                      </Row>
                      <Row className="top-row">
                        <div
                          className="top-col"
                          style={{
                            width: '35%',
                            marginTop: '30px',
                            paddingLeft: '15px',
                          }}>
                          <Form.Group as={Row} controlId="formDriver">
                            <Form.Label style={{ width: '290px' }}>Request Status</Form.Label>
                            <div className="dropdown-spinner-container">
                              <Select
                                styles={customStyles('225px')}
                                options={statusOptions}
                                isClearable
                                onChange={(event, action) => {
                                  if (action.action === 'clear') {
                                    setSearchCriteria(prev => ({ ...prev, status: '' }));
                                  }
                                  if (action.action === 'select-option') {
                                    setSearchCriteria(prev => ({ ...prev, status: event.value }));
                                  }
                                }}
                                value={statusOptions?.filter(state => state.value === searchCriteria.status)}
                              />
                            </div>
                          </Form.Group>
                        </div>
                      </Row>
                      <Row className="top-row">
                        <div
                          className="top-col"
                          style={{
                            width: '65%',
                            marginTop: '30px',
                            paddingLeft: '15px',
                          }}>
                          <Form.Group as={Row} controlId="enteredStartDate">
                            <Form.Label style={{ width: '290px' }}>Time Off Start / End Dates</Form.Label>
                            <div style={{ flexDirection: 'column' }}>
                              <Form.Control
                                style={{
                                  width: '230px',
                                  height: '30px',
                                  marginRight: '15px',
                                }}
                                as="input"
                                type="date"
                                name="startDateFrom"
                                value={searchCriteria.startDateFrom}
                                onChange={e => setSearchCriteria(prev => ({ ...prev, startDateFrom: e.target.value }))}
                              />
                            </div>
                          </Form.Group>
                          <Form.Group as={Row} controlId="enteredEndDate">
                            <Form.Label
                              style={{
                                width: '40px',
                                marginLeft: '30px',
                              }}>
                              To
                            </Form.Label>
                            <div style={{ flexDirection: 'column' }}>
                              <Form.Control
                                style={{
                                  width: '230px',
                                  height: '30px',
                                }}
                                as="input"
                                type="date"
                                name="startDateTo"
                                value={searchCriteria.startDateTo}
                                onChange={e => setSearchCriteria(prev => ({ ...prev, startDateTo: e.target.value }))}
                              />
                            </div>
                          </Form.Group>
                        </div>
                      </Row>
                      <Row className="top-row">
                        <div
                          className="top-col"
                          style={{
                            width: '65%',
                            marginTop: '30px',
                            paddingLeft: '15px',
                          }}>
                          <Form.Group as={Row} controlId="requestDateFromRef">
                            <Form.Label style={{ width: '290px' }}>Request Entered Date Range</Form.Label>
                            <div style={{ flexDirection: 'column' }}>
                              <Form.Control
                                style={{
                                  width: '230px',
                                  height: '30px',
                                  marginRight: '20px',
                                }}
                                as="input"
                                type="date"
                                name="requestDateFrom"
                                value={searchCriteria.requestDateFrom}
                                onChange={e => setSearchCriteria(prev => ({ ...prev, requestDateFrom: e.target.value }))}
                              />
                            </div>
                          </Form.Group>
                          <Form.Group as={Row} controlId="requestDateToRef">
                            <Form.Label
                              style={{
                                width: '20px',
                                marginLeft: '30px',
                                marginRight: '15px',
                              }}>
                              To
                            </Form.Label>
                            <div style={{ flexDirection: 'column' }}>
                              <Form.Control
                                style={{
                                  width: '230px',
                                  height: '30px',
                                }}
                                as="input"
                                type="date"
                                name="requestDateTo"
                                value={searchCriteria.requestDateTo}
                                onChange={e => setSearchCriteria(prev => ({ ...prev, requestDateTo: e.target.value }))}
                              />
                            </div>
                          </Form.Group>
                        </div>
                      </Row>
                    </div>
              </Form>
            }
          />
        )}
        {loading ? (
          <div className="h-100 d-flex justify-content-center p-5">
            <Spinner animation="border" />
          </div>
        ) : showResults && (
          <TimeOffRequestsTable
            data={data}
            status={TOR_STATUSES.SEARCH}
            loading={loading}
            onDataChange={handleSearch}
            isExporting={isExporting}
            setIsExporting={setIsExporting}
            customHandlers={{
              onGoToRequest: handleGoToRequest,
              onExportToExcel: handleExportToExcel,
            }}
          />
        )}
    </div>
  );
};

export default SearchTimeOffRequests;
